import React, { useMemo } from 'react';
import { useAmazonProfile } from '../../contexts/AmazonProfileContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { SELLERCLUB_SUPPORT_PHONE, SELLERCLUB_SUPPORT_PHONE_DISPLAY } from '../../constants/support';

const BLOCKING = new Set(['suspended', 'cancelled']);

export default function SubscriptionStatusBanner() {
  const { profiles } = useAmazonProfile();
  const { isAdmin } = useFeatureAccess();

  const affected = useMemo(() => (
    (profiles || []).filter((p) => {
      const st = p.subscriptionStatus;
      const phase = p.subscriptionPeriodPhase;
      const dateNotice = phase === 'ending_soon' || phase === 'past_end';
      return st === 'paused' || st === 'payment_due' || BLOCKING.has(st) || dateNotice;
    })
  ), [profiles]);

  if (isAdmin || !affected.length) return null;

  const blocking = affected.filter((p) => BLOCKING.has(p.subscriptionStatus));
  const paused = affected.filter((p) => p.subscriptionStatus === 'paused');
  const paymentDue = affected.filter((p) => p.subscriptionStatus === 'payment_due');
  const endingSoon = affected.filter((p) => p.subscriptionPeriodPhase === 'ending_soon');
  const pastEnd = affected.filter(
    (p) => p.subscriptionPeriodPhase === 'past_end' && !BLOCKING.has(p.subscriptionStatus)
  );

  const tone = blocking.length
    ? 'bg-red-50 border-red-200 text-red-950'
    : 'bg-amber-50 border-amber-200 text-amber-950';

  return (
    <div className={`border-b px-4 py-3 text-sm ${tone}`}>
      <p className="font-semibold">Service plan notice</p>
      {blocking.length > 0 && (
        <p className="mt-1">
          {blocking.length} store(s) have restricted service ({blocking.map((s) => s.storeName).slice(0, 2).join(', ')}).
          Tools may be limited until your plan is restored.
        </p>
      )}
      {paused.length > 0 && !blocking.length && (
        <p className="mt-1">
          {paused.length} store subscription(s) paused. Reporting and automation may be limited.
        </p>
      )}
      {paymentDue.length > 0 && (
        <p className="mt-1">Payment due on {paymentDue.length} store(s). Please contact billing.</p>
      )}
      {endingSoon.length > 0 && (
        <p className="mt-1">
          {endingSoon.length} store plan(s) renew within 7 days ({endingSoon.map((s) => s.storeName).slice(0, 2).join(', ')}).
        </p>
      )}
      {pastEnd.length > 0 && (
        <p className="mt-1">
          {pastEnd.length} store plan period has ended on paper — tools stay available; please renew with your account manager.
        </p>
      )}
      <p className="mt-2 text-xs opacity-90">
        Questions?{' '}
        <a href={`tel:${SELLERCLUB_SUPPORT_PHONE}`} className="font-semibold underline">
          {SELLERCLUB_SUPPORT_PHONE_DISPLAY}
        </a>
      </p>
    </div>
  );
}
